<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        AUTHENTICATION,
        Heading,
        Paragraph,
        InputUserName,
        InputEmail,
        InputOrganizationName,
        InputUrl,
        InputMobile,
        SubscriptionForm,
        SESSION_USER,
        Button,
        IS_AUTHENTICATED,
        SUBSCRIPTION_API,
        ProgressLines,
        FieldsetPrice,
        FieldsetVolume,
        FieldsetProducts,
        FieldsetOtherSpecs,
        SESSION_API,
        SelectOrganizationTypes,
        SelectChannels,
        SelectMarkets,
        Form,
        addMessage,
    } from '@smatch/cassiopeia-web-components';

    // LAYOUTS
    import LayoutLanding from 'src/app/library/layouts/layout-landing/LayoutLanding.svelte';

    // TYPES
    import type {
        Subscription,
        Organization,
        FormComponent,
        OrganizationTypes,
        OrganizationChannels,
    } from 'src/app/types';
    import type { Writable } from 'svelte/store';
    import type { RegisterRequest } from '@smatch/cassiopeia-web-components/dist/scopes/authentication/services/api';

    /* TYPES ----------------------------------------------------------------------------------- */

    type Step = '1' | '2' | '3' | '4' | '5';

    /* INITIALIZE ------------------------------------------------------------------------------ */

    let SubscriptionFormElement: FormComponent | null = null;

    $: SUBSCRIPTION = SubscriptionFormElement === null
        ? undefined
        : SubscriptionFormElement.DATA as Writable<Subscription>;

    const STEPS = {
        1: 'Step 1',
        2: 'Step 2',
        3: 'Step 3',
        4: 'Step 4',
        5: 'Step 5',
        6: 'Step 6',
    };

    let currentStep: Step = '1';

    let organizationTypes: OrganizationTypes = [];
    let channels: OrganizationChannels = [];
    let markets: string[] = [];

    let stepOneError: string = '';

    /* INTERNALS -------------------------------------------------------------------------------- */

    const goToStep = (step: Step): void => {
        currentStep = step;
    };

    const shouldShowExtraOrganizationFields = (organizationTypes: Organization['types']): boolean => {
        if (organizationTypes === undefined || organizationTypes.length === 0) {
            return false;
        }

        if (organizationTypes.includes('buyer_wholesaler')) {
            return organizationTypes.length > 1;
        }
        return true;
    };

    $: showAdditionalFields = shouldShowExtraOrganizationFields(organizationTypes);

    // eslint-disable-next-line max-lines-per-function
    const registerUser = async (): Promise<void> => {
        const REGISTRATION_REQUEST = {
            organization: {
                channelCodes: channels,
                domain: 'buyer',
                marketCodes: markets,
                name: $AUTHENTICATION.companyName,
                types: organizationTypes,
                url: $AUTHENTICATION.url,
            },
            user: {
                email: $AUTHENTICATION.email!,
                mobile: $AUTHENTICATION.mobile,
                name: $AUTHENTICATION.name!,
            },
        };

        const RESPONSE = await SESSION_API.register(REGISTRATION_REQUEST as RegisterRequest);

        if ('error' in RESPONSE) {
            stepOneError = RESPONSE.error;
        } else {
            stepOneError = '';
        }
    };

    const submitData = async (): Promise<void> => {
        await registerUser();

        if (Boolean(stepOneError)) {
            addMessage({
                heading: 'Please double check your information.',
                type: 'warning',
            });
            goToStep('1');
            return;
        }

        if ($SESSION_USER === null || $SUBSCRIPTION === undefined) {
            return;
        }

        $SUBSCRIPTION.ownerKey = $SESSION_USER._.key;
        stepOneError = '';

        if (!(SubscriptionFormElement as FormComponent).isValid()) {
            return;
        }

        void (SubscriptionFormElement as FormComponent).submit();
    };

    const onSave = (): void => {
        // For multiple brands, the create endpoint will only return a 204, which doesn't update the SUBSCRIPTION_STORE (and therefore makes transition to the next page impossible)
        void SUBSCRIPTION_API.filterSubscriptions({ ownerKey: $SESSION_USER?._.key });
    };

</script>

<!-- eslint-disable max-lines -->

<LayoutLanding>

    <Heading size = 'xl'>New Subscription</Heading>
    <Paragraph>
        Welcome to our Sourcing Agent.
        Please fill out the fields below and we will find the right stock for you.
    </Paragraph>

    <SubscriptionForm
        action = 'create'
        bind:this = { SubscriptionFormElement }
        on:save = { onSave }
    >

        {#if $SUBSCRIPTION !== undefined}
            <div class = 's8-sourcing-agent-form'>

                <ProgressLines steps = { STEPS } current = { currentStep } />

                <!-- Step 1: organization and contact information -->
                {#if currentStep === '1' && !$IS_AUTHENTICATED}

                    <Heading size='xs'>How do we get in touch with you?</Heading>
                    <Form onSubmit = { goToStep.bind(null, '2') } isSubmittedOnEnter = { false }>
                        {#if Boolean(stepOneError)}
                            <Paragraph label = { stepOneError } css = 'mt-4' color = 'error' />
                        {/if}
                        <div class = 's8-row-user'>
                            <InputUserName bind:value = { $AUTHENTICATION.name } />
                            <InputEmail bind:value = { $AUTHENTICATION.email } />
                        </div>
                        <div class = 's8-row-org'>
                            <InputOrganizationName bind:value = { $AUTHENTICATION.companyName } />
                            <InputUrl bind:value = { $AUTHENTICATION.url } isRequired = { false }/>
                            <InputMobile bind:value = { $AUTHENTICATION.mobile } isRequired = { true }/>
                        </div>
                        <div class = 's8-single-button'>
                            <Button label = 'Next' type = 'submit' />
                        </div>
                    </Form>

                <!-- Step 2: background on the organization -->
                {:else if currentStep === '2'}

                    <Heading size='xs'>What can you tell us about your business?</Heading>
                    <Form onSubmit = { goToStep.bind(null, '3') } isSubmittedOnEnter = { false }>
                        <div class = 's8-organization'>
                            <SelectOrganizationTypes
                                bind:keys = { organizationTypes }
                                label = { null }
                                placeholder = 'What best describes your business?'
                                domain = 'buyer'
                                minItems = { 0 }
                            />
                            {#if showAdditionalFields}
                                <SelectChannels
                                    label = 'On which channels do you sell?'
                                    bind:keys = { channels }
                                    minItems = { 0 }
                                />

                                <SelectMarkets
                                    label = 'On which markets do you sell?'
                                    bind:keys = { markets }
                                    minItems = { 0 }
                                />
                            {/if}
                        </div>
                        <div class = 's8-button-group'>
                            <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '1') } />
                            <Button label = 'Next' type = 'submit' />
                        </div>
                    </Form>

                <!-- Step 3: products specifications -->
                {:else if currentStep === '3'}

                    <Form onSubmit = { goToStep.bind(null, '4') } isSubmittedOnEnter = { false }>
                        <FieldsetProducts bind:subscription = { $SUBSCRIPTION } />
                        <div class = 's8-button-group'>
                            <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '2') } />
                            <Button label = 'Next' type = 'submit' />
                        </div>
                    </Form>

                <!-- Step 4: price specification -->
                {:else if currentStep === '4'}

                    <Form onSubmit = { goToStep.bind(null, '5') } isSubmittedOnEnter = { false }>
                        <FieldsetPrice bind:subscription = { $SUBSCRIPTION } />
                        <div class = 's8-button-group'>
                            <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '3') } />
                            <Button label = 'Next' type = 'submit' />
                        </div>
                    </Form>

                <!-- Step 5: volume specification -->
                {:else if currentStep === '5'}

                    <Form onSubmit = { goToStep.bind(null, '6') } isSubmittedOnEnter = { false }>
                        <FieldsetVolume bind:subscription = { $SUBSCRIPTION } />
                        <div class = 's8-button-group'>
                            <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '4') } />
                            <Button label = 'Next' on:click = { goToStep.bind(null, '6') } />
                        </div>
                    </Form>

                <!-- Step 6: all the other specs -->
                {:else}

                    <FieldsetOtherSpecs bind:subscription = { $SUBSCRIPTION } />
                    <div class = 's8-button-group'>
                        <Button label = 'Back' style = 'outline' on:click = { goToStep.bind(null, '5') } />
                        <Button label = 'Create Your Subscription' on:click = { submitData } />
                    </div>

                {/if}

            </div>
        {/if}

    </SubscriptionForm>

</LayoutLanding>

<style lang='postcss'>

    div.s8-button-group {
        display: flex;
        justify-content: space-between;
    }

    div.s8-single-button {
        justify-self: self-end;
    }

    div[class*='s8-row'] {
        display: grid;
        column-gap: 20px;

        &.s8-row-user {
            grid-template-columns: 2fr 1fr;
        }

        &.s8-row-org {
            grid-template-columns: 1fr 1fr 1fr;
            margin-bottom: 15px;
        }
    }

    div.s8-sourcing-agent-form {
        :global(ul.s8-progress-lines) {
            margin-top: 35px;
            margin-bottom: 10px;
        }
    }

    div.s8-organization {
        margin-bottom: 15px;
    }

    @media (max-width: 780px) {
        div[class*='s8-row'] {
            display: flex;
            flex-direction: column;
        }
    }
</style>